/**
 * ArbitrationRoom.jsx
 *
 * WhatsApp-style arbitration chat room for Buyers, Bidders, Arbitrators, and Admins.
 *
 * All four participant types see:
 *   - Full message history (all senders)
 *   - Participant list panel
 *   - Evidence / document panel
 *   - File upload (PDF, DOC, DOCX, XLSX, PNG, JPG, JPEG)
 *
 * Admin additionally sees:
 *   - Invite Buyer / Invite Bidder / Assign Arbitrator controls
 *   - Final Decision panel (RESOLVED / UNRESOLVED) with mandatory justification
 *
 * Arbitrator additionally sees:
 *   - Final Decision (resolve for buyer or bidder — closes room and updates dispute/deal)
 *
 * Messages sent by the current user appear on the RIGHT (blue).
 * Messages from others appear on the LEFT (dark card).
 * System messages appear centred in amber.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, Card, Chip, CircularProgress, Divider,
  Grid, IconButton, MenuItem, Paper, Select, TextField, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Tooltip, Badge,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import GavelIcon from '@mui/icons-material/Gavel';
import ChatIcon from '@mui/icons-material/Chat';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { apiClient } from '../../utils/auth';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';

// ─── Role styling ────────────────────────────────────────────────────────────
const ROLE_META = {
  ADMIN:      { label: 'Admin',       color: '#a78bfa', bg: '#7c3aed', chip: 'secondary' },
  BUYER:      { label: 'Buyer',       color: '#60a5fa', bg: '#1d4ed8', chip: 'primary'   },
  BIDDER:     { label: 'Bidder',      color: '#34d399', bg: '#065f46', chip: 'success'   },
  ARBITRATOR: { label: 'Arbitrator',  color: '#fbbf24', bg: '#92400e', chip: 'warning'   },
  SYSTEM:     { label: 'System',      color: '#94a3b8', bg: '#1e293b', chip: 'default'   },
};

function roleInitial(role) {
  return (ROLE_META[role]?.label || role || '?')[0].toUpperCase();
}

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Attachment bubble (inline in chat) ──────────────────────────────────────
function AttachmentBubble({ content, attachments, isMine }) {
  const match = content.match(/\[ATTACHMENT\]\s+(.+?)\s+\(attachment_id:(\d+)\)/);
  if (!match) {
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{content}</Typography>;
  }
  const [, fileName, attIdStr] = match;
  const att = attachments.find((a) => a.id === parseInt(attIdStr, 10));
  const baseURL = getApiBaseUrl();
  const href = att ? `${baseURL}/media/${att.file_path}` : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200, maxWidth: 280 }}>
      <InsertDriveFileIcon sx={{ color: isMine ? 'rgba(255,255,255,0.9)' : '#818cf8', fontSize: 28, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
          {att ? att.file_name : fileName}
        </Typography>
        {att && (
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            {formatBytes(att.file_size)}
          </Typography>
        )}
      </Box>
      {href && (
        <Tooltip title="Download">
          <IconButton size="small" component="a" href={href} target="_blank" rel="noopener noreferrer"
            sx={{ color: isMine ? 'rgba(255,255,255,0.85)' : '#818cf8', flexShrink: 0 }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// ─── Participant panel (shown to ALL roles) ───────────────────────────────────
function ParticipantPanel({ room }) {
  const participants = [
    { role: 'BUYER',      label: 'Buyer Party',          id: room.buyer_anonymous_id  ? `ID: ${room.buyer_anonymous_id.slice(0,8)}…` : 'Invited' },
    { role: 'BIDDER',     label: 'Bidder Party',         id: room.bidder_anonymous_id ? `ID: ${room.bidder_anonymous_id.slice(0,8)}…` : 'Invited' },
    { role: 'ARBITRATOR', label: 'Arbitrator',           id: room.arbitrator ? (room.arbitrator.full_name || 'Assigned') : 'Not assigned yet' },
    { role: 'ADMIN',      label: 'Administrator',        id: 'Supervising' },
  ];

  return (
    <Card sx={{ bgcolor: '#0d1526', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, mb: 2 }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupIcon sx={{ color: '#818cf8', fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#e2e8f0' }}>Participants</Typography>
        <Chip label={participants.length} size="small" sx={{ ml: 'auto', bgcolor: 'rgba(129,140,248,0.15)', color: '#818cf8', fontWeight: 700, height: 20 }} />
      </Box>
      <Box sx={{ p: 1.5 }}>
        {participants.map((p) => {
          const meta = ROLE_META[p.role] || ROLE_META.SYSTEM;
          return (
            <Box key={p.role} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, '&:last-child': { mb: 0 } }}>
              <Avatar sx={{ bgcolor: meta.bg, width: 34, height: 34, fontSize: 14, fontWeight: 700 }}>
                {roleInitial(p.role)}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#f1f5f9', lineHeight: 1.2 }}>{p.label}</Typography>
                <Typography variant="caption" sx={{ color: meta.color }}>{p.id}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
}

// ─── Evidence panel (shown to ALL roles) ─────────────────────────────────────
function EvidencePanel({ attachments }) {
  const baseURL = getApiBaseUrl();
  return (
    <Card sx={{ bgcolor: '#0d1526', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FolderOpenIcon sx={{ color: '#818cf8', fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#e2e8f0' }}>Evidence</Typography>
        <Chip label={attachments.length} size="small" sx={{ ml: 'auto', bgcolor: 'rgba(129,140,248,0.15)', color: '#818cf8', fontWeight: 700, height: 20 }} />
      </Box>
      <Box sx={{ p: 1.5, maxHeight: 260, overflowY: 'auto' }}>
        {attachments.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No documents uploaded yet.</Typography>
        ) : (
          attachments.map((a) => (
            <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, '&:last-child': { mb: 0 } }}>
              <InsertDriveFileIcon sx={{ color: '#818cf8', fontSize: 20, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#e2e8f0', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.file_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ROLE_META[a.uploader_role]?.label || a.uploader_role} · {formatBytes(a.file_size)}
                </Typography>
              </Box>
              <Tooltip title="Download / View">
                <IconButton size="small" component="a" href={`${baseURL}/media/${a.file_path}`} target="_blank" rel="noopener noreferrer" sx={{ color: '#818cf8', flexShrink: 0 }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))
        )}
      </Box>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ArbitrationRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userRole = typeof window !== 'undefined' ? localStorage.getItem('cmp_role') || '' : '';
  const isAdmin = userRole === 'ADMIN';
  const isArbitrator = userRole === 'ARBITRATOR';

  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Chat input
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  // File upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Admin: final decision dialog
  const [decisionDialog, setDecisionDialog] = useState(false);
  const [outcome, setOutcome] = useState('RESOLVED');
  const [justification, setJustification] = useState('');
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionSuccess, setDecisionSuccess] = useState('');

  // Admin: assign arbitrator dialog
  const [assignDialog, setAssignDialog] = useState(false);
  const [arbitratorId, setArbitratorId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Admin: invite
  const [inviteLoading, setInviteLoading] = useState(false);

  // Arbitrator: final decision dialog
  const [arbDecisionDialog, setArbDecisionDialog] = useState(false);
  const [arbResolution, setArbResolution] = useState('RESOLVED_BUYER');
  const [arbDecisionReason, setArbDecisionReason] = useState('');
  const [arbDecisionLoading, setArbDecisionLoading] = useState(false);

  const chatBottomRef = useRef(null);

  // ── Fetch room data ──────────────────────────────────────────────────────
  const fetchRoom = useCallback(async () => {
    try {
      // Admin uses the full-detail endpoint; everyone else uses the user endpoint
      const endpoint = isAdmin
        ? `/api/v1/admin/arbitrations/${id}/full/`
        : `/api/v1/arbitrations/${id}/`;
      const { data } = await apiClient.get(endpoint);
      setRoom(data);
      setMessages(data.messages || []);
      if (data.attachments) setAttachments(data.attachments);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to connect to the arbitration room.');
    } finally {
      setLoading(false);
    }
  }, [id, isAdmin]);

  useEffect(() => {
    fetchRoom();
    // Poll every 4 seconds for new messages
    const interval = setInterval(fetchRoom, 4000);
    return () => clearInterval(interval);
  }, [fetchRoom]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message ─────────────────────────────────────────────────────────
  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    setSendError('');
    try {
      const endpoint = isAdmin
        ? `/api/v1/admin/arbitrations/${id}/messages/`
        : `/api/v1/arbitrations/${id}/messages/`;
      const { data } = await apiClient.post(endpoint, { message: text.trim() });
      // Append immediately; polling will sync the rest
      setMessages((prev) => {
        // Avoid duplicate if polling already added it
        if (prev.find((m) => m.id === data.id)) return prev;
        return [...prev, data];
      });
      setText('');
    } catch (err) {
      setSendError(err.response?.data?.error?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  // ── File upload ──────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post(`/api/v1/arbitrations/${id}/attachments/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Refresh to get the new attachment + system message
      await fetchRoom();
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Upload failed. Check file type and size (max 20 MB).');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Admin: final decision ────────────────────────────────────────────────
  const handleFinalDecision = async () => {
    if (!justification.trim()) { alert('Justification is mandatory.'); return; }
    setDecisionLoading(true);
    try {
      await apiClient.post(`/api/v1/admin/arbitrations/${id}/final-decision/`, { outcome, justification });
      setDecisionSuccess(`Decision recorded: ${outcome}. Room is now closed.`);
      setDecisionDialog(false);
      setTimeout(() => navigate('/admin/disputes'), 1800);
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to submit decision.');
    } finally {
      setDecisionLoading(false);
    }
  };

  // ── Admin: assign arbitrator ─────────────────────────────────────────────
  const handleAssignArbitrator = async () => {
    if (!arbitratorId.trim()) { alert('Enter an Arbitrator User ID.'); return; }
    setAssigning(true);
    try {
      await apiClient.post(`/api/v1/admin/arbitrations/${id}/assign-arbitrator/`, {
        arbitrator_id: parseInt(arbitratorId, 10),
      });
      setAssignDialog(false);
      setArbitratorId('');
      fetchRoom();
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to assign arbitrator.');
    } finally {
      setAssigning(false);
    }
  };

  // ── Admin: invite ────────────────────────────────────────────────────────
  const handleInvite = async (buyer, bidder) => {
    setInviteLoading(true);
    try {
      await apiClient.post(`/api/v1/admin/arbitrations/${id}/invite/`, {
        invite_buyer: buyer,
        invite_bidder: bidder,
      });
      alert(`Notification sent to ${buyer && bidder ? 'Buyer and Bidder' : buyer ? 'Buyer' : 'Bidder'}.`);
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to send invitation.');
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Arbitrator: binding final decision ───────────────────────────────────
  const handleArbFinalDecision = async () => {
    if (!arbDecisionReason.trim()) { alert('Justification is required.'); return; }
    setArbDecisionLoading(true);
    try {
      const { data } = await apiClient.post(`/api/v1/arbitrations/${id}/final-decision/`, {
        resolution: arbResolution,
        decision_reason: arbDecisionReason.trim(),
      });
      setDecisionSuccess(
        `Final decision recorded (${data.dispute_status}). The arbitration room is now closed.`
      );
      setArbDecisionDialog(false);
      setArbDecisionReason('');
      setTimeout(() => navigate('/arbitrations/mine'), 1800);
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to submit final decision.');
    } finally {
      setArbDecisionLoading(false);
    }
  };

  // ── Loading / error states ───────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', bgcolor: '#080d1a' }}>
        <CircularProgress sx={{ color: '#818cf8' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="error" variant="outlined">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(isAdmin ? '/admin/disputes' : '/deals/mine')}
          sx={{ mt: 3, textTransform: 'none', color: '#94a3b8' }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const roomOpen = room.status === 'ACTIVE';
  const canChat = roomOpen; // all participants can chat while room is active

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#080d1a', color: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', bgcolor: '#0d1526' }}>
        <IconButton onClick={() => navigate(isAdmin ? '/admin/disputes' : '/deals/mine')} sx={{ color: '#94a3b8', mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h6" fontWeight={700}>Arbitration Room #{room.id}</Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Deal #{room.deal_id} · {roomOpen ? 'Active' : 'Closed'}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          {isAdmin && roomOpen && (
            <>
              <Button size="small" variant="outlined" color="primary" startIcon={<PersonAddIcon />} onClick={() => handleInvite(true, true)}>
                Invite Parties
              </Button>
              <Button size="small" variant="outlined" color="warning" startIcon={<AccountCircleIcon />} onClick={() => setAssignDialog(true)}>
                Assign Arbitrator
              </Button>
              <Button size="small" variant="contained" color="error" startIcon={<GavelIcon />} onClick={() => setDecisionDialog(true)}>
                Final Decision
              </Button>
            </>
          )}
          {isArbitrator && roomOpen && !room.decision && (
            <Button size="small" variant="contained" color="error" startIcon={<GavelIcon />} onClick={() => setArbDecisionDialog(true)}>
              Final Decision
            </Button>
          )}
        </Box>
      </Box>

      {/* Main content */}
      <Grid container sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Left: Chat */}
        <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.08)', height: 'calc(100vh - 72px)' }}>
          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: '#080d1a' }}>
            {messages.map((m) => {
              const isSys = m.sender_role === 'SYSTEM';
              if (isSys) {
                return (
                  <Box key={m.id} sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Box sx={{ bgcolor: 'rgba(251,191,36,0.1)', color: '#fbbf24', px: 2, py: 0.5, borderRadius: 4, fontSize: 13, border: '1px solid rgba(251,191,36,0.2)' }}>
                      {m.content}
                    </Box>
                  </Box>
                );
              }
              const isMine = m.is_mine;
              const meta = ROLE_META[m.sender_role] || ROLE_META.SYSTEM;
              
              // Handle attachment inline rendering
              const hasAttachment = m.content.includes('[ATTACHMENT]');

              return (
                <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexDirection: isMine ? 'row-reverse' : 'row', maxWidth: '75%' }}>
                    {!isMine && (
                      <Avatar sx={{ bgcolor: meta.bg, width: 28, height: 28, fontSize: 12, mb: 1 }}>{roleInitial(m.sender_role)}</Avatar>
                    )}
                    <Box>
                      {!isMine && (
                        <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600, ml: 1, display: 'block', mb: 0.5 }}>
                          {meta.label} {m.sender ? `(${m.sender})` : ''}
                        </Typography>
                      )}
                      <Paper sx={{
                        p: 1.5,
                        bgcolor: isMine ? '#4f46e5' : '#1e293b',
                        color: isMine ? '#fff' : '#f1f5f9',
                        borderRadius: 2,
                        borderTopRightRadius: isMine ? 0 : 2,
                        borderTopLeftRadius: !isMine ? 0 : 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      }}>
                        {hasAttachment ? (
                          <AttachmentBubble content={m.content} attachments={attachments} isMine={isMine} />
                        ) : (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{m.content}</Typography>
                        )}
                      </Paper>
                      <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block', textAlign: isMine ? 'right' : 'left' }}>
                        {formatTime(m.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={chatBottomRef} />
          </Box>

          {/* Input Area */}
          {canChat && (
            <Box sx={{ p: 2, bgcolor: '#0d1526', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              {sendError && <Alert severity="error" sx={{ mb: 2, py: 0 }}>{sendError}</Alert>}
              {uploadError && <Alert severity="error" sx={{ mb: 2, py: 0 }}>{uploadError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.xlsx,.png,.jpg,.jpeg"
                />
                <Tooltip title="Upload Document">
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    sx={{ color: '#94a3b8', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    {uploading ? <CircularProgress size={24} color="inherit" /> : <AttachFileIcon />}
                  </IconButton>
                </Tooltip>
                <TextField
                  fullWidth
                  placeholder="Type a message..."
                  variant="outlined"
                  size="small"
                  multiline
                  maxRows={4}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1e293b',
                      color: '#f8fafc',
                      '& fieldset': { border: 'none' },
                    }
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={sending || !text.trim()}
                  sx={{ bgcolor: '#4f46e5', color: '#fff', '&:hover': { bgcolor: '#4338ca' }, '&.Mui-disabled': { bgcolor: '#334155', color: '#64748b' } }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </Grid>

        {/* Right: Panels */}
        <Grid item xs={12} md={4} sx={{ p: 3, bgcolor: '#080d1a', borderLeft: '1px solid rgba(255,255,255,0.08)', overflowY: 'auto', height: 'calc(100vh - 72px)' }}>
          {room.decision && (
            <Alert severity={room.decision.decision_type.includes('BUYER') ? 'success' : 'info'} sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="subtitle2" fontWeight={700}>Decision: {room.decision.decision_type.replace(/_/g, ' ')}</Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{room.decision.decision_reason}</Typography>
            </Alert>
          )}

          <ParticipantPanel room={room} />
          <EvidencePanel attachments={attachments} />
        </Grid>
      </Grid>

      {/* Admin Dialogs */}
      <Dialog open={decisionDialog} onClose={() => setDecisionDialog(false)} PaperProps={{ sx: { bgcolor: '#1e293b', color: '#fff' } }}>
        <DialogTitle>Issue Final Decision</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel sx={{ color: '#94a3b8' }}>Outcome</InputLabel>
            <Select value={outcome} onChange={(e) => setOutcome(e.target.value)} label="Outcome" sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#475569' } }}>
              <MenuItem value="RESOLVED">Resolved (Close Dispute)</MenuItem>
              <MenuItem value="UNRESOLVED">Unresolved (Dismiss Dispute)</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth multiline rows={4} label="Justification" value={justification} onChange={(e) => setJustification(e.target.value)} required InputLabelProps={{ sx: { color: '#94a3b8' } }} sx={{ input: { color: '#fff' }, '.MuiOutlinedInput-root': { '& fieldset': { borderColor: '#475569' }, '&:hover fieldset': { borderColor: '#64748b' } } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDecisionDialog(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
          <Button onClick={handleFinalDecision} variant="contained" color="error" disabled={decisionLoading || !justification.trim()}>{decisionLoading ? 'Submitting...' : 'Submit Decision'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} PaperProps={{ sx: { bgcolor: '#1e293b', color: '#fff' } }}>
        <DialogTitle>Assign Arbitrator</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>Enter the User ID of an approved arbitrator to assign to this room.</Typography>
          <TextField fullWidth label="Arbitrator User ID" type="number" value={arbitratorId} onChange={(e) => setArbitratorId(e.target.value)} InputLabelProps={{ sx: { color: '#94a3b8' } }} sx={{ input: { color: '#fff' }, '.MuiOutlinedInput-root': { '& fieldset': { borderColor: '#475569' } } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setAssignDialog(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
          <Button onClick={handleAssignArbitrator} variant="contained" color="warning" disabled={assigning || !arbitratorId.trim()}>{assigning ? 'Assigning...' : 'Assign'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={arbDecisionDialog} onClose={() => setArbDecisionDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#1e293b', color: '#fff' } }}>
        <DialogTitle>Issue Final Decision</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
            This closes the arbitration room, updates the dispute outcome, restores the deal from arbitration pause, and releases escrow holds.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel sx={{ color: '#94a3b8' }}>Outcome</InputLabel>
            <Select
              value={arbResolution}
              onChange={(e) => setArbResolution(e.target.value)}
              label="Outcome"
              sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#475569' } }}
            >
              <MenuItem value="RESOLVED_BUYER">Resolved in favour of Buyer</MenuItem>
              <MenuItem value="RESOLVED_BIDDER">Resolved in favour of Bidder</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Justification"
            value={arbDecisionReason}
            onChange={(e) => setArbDecisionReason(e.target.value)}
            required
            InputLabelProps={{ sx: { color: '#94a3b8' } }}
            sx={{ input: { color: '#fff' }, '.MuiOutlinedInput-root': { '& fieldset': { borderColor: '#475569' }, '&:hover fieldset': { borderColor: '#64748b' } } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setArbDecisionDialog(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
          <Button onClick={handleArbFinalDecision} variant="contained" color="error" disabled={arbDecisionLoading || !arbDecisionReason.trim()}>
            {arbDecisionLoading ? 'Submitting...' : 'Submit Final Decision'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
